import Rails from '@rails/ujs'
import { useDispatch, useClickOutside } from 'stimulus-use'
import template from 'lodash.template'
import ApplicationController from '@app/javascript/controllers/application_controller'
import { truncate } from '@app/javascript/classes/stringExtensions'

export default class extends ApplicationController {
  static targets = [
    'dropdown',
    'filter',
    'listContainer',
    'loadingIndicator',
    'menuItem',
    'selectionsList',
    'selectionPillTemplate',
    'submit',
    'tag',
    'tagsContainer',
    'tagTemplate'
  ]

  static values = {
    url: String,
    urlToFetchSelectedItems: String,
    preSelectedItems: Array,
    showDropdownClass: String,
    policy: Object,

    // Optional Boolean value, defaults to true.
    // Allows to show or not show the selected items as pills.
    selectionsListEnabled: Boolean,
    skipPillTruncation: Boolean, // pill labels ae truncated by default
    alphabeticalOrder: Boolean,
    initialFetchItems: Boolean
  }

  static attributeToRejectUpdatesName = `data-${this.identifier}-reject-updates`

  doConnect() {
    if (!this.hasUrlValue && this.hasLoadingIndicatorTarget) {
      this.loadingDone()
    }
    this.shouldTruncatePills = !this.skipPillTruncationValue
    this.showDropdownClass = this.showDropdownClassValue || 'c-select-multiple__menu--active'
    this.menuItemsSelected = []
    this.id = this.data.get('id') | null
    useClickOutside(this)
    useDispatch(this)
    if (this.hasSelectionPillTemplateTarget) {
      this.selectionPillTemplate = template(this.selectionPillTemplateTarget.innerHTML)
    }

    // Defaults value to show selections as pills to true
    if (!this.hasSelectionsListEnabledValue) { this.selectionsListEnabledValue = true }

    // NOTE: When this Stimulus controller is being used without an underlying
    // +SelectMultiple::List+ component, the callback +onListLoaded+ will never be fired.
    // For this reason, we fire the callback manually so things can work seamlessly.
    if (!this.selectMultipleListComponent) {
      this.onListLoaded()
    }

    if (this.initialFetchItemsValue) {
      this.fetchItems()
    }
  }

  get selectMultipleListComponent() {
    const el = this.element.querySelector('[data-controller~="shared--select-multiple--list"]')

    return el && el['shared--select-multiple--list']
  }

  get selectionsList() {
    if (this.hasSelectionsListTarget) {
      return this.selectionsListTarget
    } else if (this.hasTagsContainerTarget) {
      return this.tagsContainerTarget
    }
  }

  get canUpdate() {
    return !this.element.hasAttribute(this.attributeToRejectUpdatesName)
  }

  onListLoaded() {
    this.checkAllMenuItemsSelected()
    this.addMenuItemsCheckedAsSelected()
    this.convertSelectionIntoPills()

    this.dispatch('listLoaded')
  }

  showSelectionsList() {
    this.selectionsList.removeAttribute('hidden')
  }

  hideSelectionsList() {
    this.selectionsList.setAttribute('hidden', 'hidden')
  }

  clearAll({ silent } = {}) {
    if (this.canUpdate) {
      this.menuItemsSelected = []

      if (this.selectionsList) {
        this.selectionsList.innerHTML = ''
      }

      this.menuItemTargets.forEach((elem) => {
        elem.checked = false
      })

      if (!silent) {
        this.filterTarget.dispatchEvent(new CustomEvent('search-filter:clear'))
      }
    }
  }

  clearValue(selectedValue) {
    if (this.canUpdate) {
      const selectedPillNode = this.selectionsList ? this.selectionsList.querySelector(`[data-pill-value="${selectedValue}"]`) : null
      this.clearSelectionByPill(selectedPillNode)
    }
  }

  // For the values, the user will be able to select them, but they won't be
  // part of the form submission. For this, we simply remove the 'name' attribute
  // from the <input> elem.
  setItemsAsNonSubmittable() {
    this.menuItemTargets.forEach((menuItem) => {
      if (menuItem.getAttribute('name')) {
        menuItem.setAttribute('name-disabled', menuItem.getAttribute('name'))
        menuItem.removeAttribute('name')
      }
    })
  }

  // Basically the opposite of +setItemsAsNonSubmittable+, ie, we restore the input's 'name' attribute.
  restoreItemsAsSubmittable() {
    this.menuItemTargets.forEach((menuItem) => {
      if (menuItem.getAttribute('name-disabled')) {
        menuItem.setAttribute('name', menuItem.getAttribute('name-disabled'))
        menuItem.removeAttribute('name-disabled')
      }
    })
  }

  selectValues(values, { submit } = {}) {
    this.setValuesSelected(values, true, { submit })
  }

  deselectValues(values, { submit } = {}) {
    this.setValuesSelected(values, false, { submit })
  }

  deselectAll({ submit } = {}) {
    this.setValuesSelected([], false, { submit, all: true })
  }

  setValuesSelected(values, selected, { submit, all } = {}) {
    this.menuItemTargets.forEach((menuItem) => {
      if (all || values.includes(menuItem.getAttribute('value'))) {
        this.setCheckMenuItem(menuItem, selected)

        if (selected) {
          this.addMenuItemAsSelected(menuItem)
        } else {
          this.removeMenuItemFromSelected(menuItem)
        }
      }
    })

    if (this.isDropdownActive()) {
      this.resolveSelectionStateForParentCheckboxes()
    }

    if (submit) {
      this.submitCheckedMenuItemsAsSelected()
    }
  }

  getSelectedValues() {
    const values = []

    this.menuItemsSelected.forEach((menuItem) => {
      // Checkbox items with no HTML 'name' attribute means that they aren't counted
      // on form submissions, so they are irrelevant for this function.
      if (menuItem.getAttribute('name') || menuItem.getAttribute('name-disabled')) {
        values.push(menuItem.getAttribute('value'))
      }
    })

    return values
  }

  resetState() {
    this.clearSearchInput()
    this.uncheckAll()
  }

  reloadSelectedItems() {
    if (!this.itemsFetched) return

    this.menuItemsSelected = []
    this.fetchSelectedItems()
  }

  clearSearchInput() {
    this.filterTarget.value = ''
  }

  submitCheckedMenuItemsAsSelected(event) {
    this.dispatch('beforeSubmit')
    this.convertSelectionIntoPills()
    this.closeDropdown()
    this.dispatch('submitted')
  }

  allowUpdates(event) {
    this.element.removeAttribute(this.attributeToRejectUpdatesName)
  }

  rejectUpdates(event) {
    this.element.setAttribute(this.attributeToRejectUpdatesName, 'true')
  }

  fetchSelectedItems() {
    if (!this.hasUrlToFetchSelectedItemsValue) { return }

    Rails.ajax({
      type: 'GET',
      url: this.urlToFetchSelectedItemsValue,
      dataType: 'json',
      success: (response) => {
        const selectedIds = response.map(i => i.id.toString())

        this.menuItemTargets
          .filter((menuItem) => selectedIds.includes(menuItem.value.toString()))
          .forEach((menuItem) => this.addMenuItemAsSelected(menuItem))
      }
    })
  }

  reloadData() {
    let selectedIds = []
    this.listContainerTarget.innerHTML = null

    Rails.ajax({
      type: 'GET',
      url: this.urlToFetchSelectedItemsValue,
      dataType: 'json',
      success: (response) => {
        selectedIds = response.map(i => i.id.toString())

        Rails.ajax({
          type: 'GET',
          url: this.urlValue,
          dataType: 'html',
          data: $.param({
            checked_ids: selectedIds
          }),
          success: (response) => {
            this.listContainerTarget.innerHTML = response.body.innerHTML
            this.menuItemsSelected = []
            this.menuItemTargets.forEach((menuItem) => {
              if (menuItem.checked) {
                this.addMenuItemAsSelected(menuItem)
              }
            })
            this.dispatch('reload-completed')
            this.convertSelectionIntoPills()
          }
        })
      }
    })
  }

  // TODO: Remove. Deprecated. 'retrieveTags' is misleading. Kept for backwards compatibility.
  retrieveTags() {
    this.fetchItems()
  }

  fetchItems() {
    if (!this.hasUrlValue) { return }
    if (this.itemsFetched) { return }
    this.loading()
    this.listContainerTarget.innerHTML = null

    Rails.ajax({
      type: 'GET',
      url: this.urlValue,
      dataType: 'html',
      data: $.param({
        checked_ids: this.getMenuItemsSelectedValues()
      }),
      success: (response) => {
        this.itemsFetched = true
        this.listContainerTarget.innerHTML = response.body.innerHTML
        this.menuItemsSelected = []

        this.menuItemTargets.forEach((menuItem) => {
          if (menuItem.checked) {
            this.addMenuItemAsSelected(menuItem)
          } else if (this.preSelectedItemsValue.includes(menuItem.value.toString())) {
            this.addMenuItemAsSelected(menuItem)
          }
        })

        this.loadingDone()
        this.fetchSelectedItems()
        this.convertSelectionIntoPills()
        this.dispatch('fetchItems-completed')
      },
      error: () => {
        this.listContainerTarget.innerHTML =
          'An error occurred. Please refresh the page and try again.'
        this.loadingDone()
      }
    })
  }

  loading() {
    this.loadingIndicatorTarget.classList.remove('hidden')
  }

  loadingDone() {
    this.loadingIndicatorTarget.classList.add('hidden')
  }

  policy(canOperation) {
    return this.hasPolicyValue ? this.policyValue[canOperation] : true // default allows everything
  }

  // ------ Functions dealing with the Dropdown state --------------------------

  toggleDropdown(event) {
    event.preventDefault()

    if (event.target.getAttribute('disabled')) { return }
    if (event.target.closest('div').getAttribute('disabled')) { return }

    if (this.isDropdownActive()) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  clickOutside(event) {
    if (this.isDropdownActive()) {
      this.closeDropdown()
    }
  }

  isDropdownActive() {
    return this.dropdownTarget.classList.contains(this.showDropdownClass)
  }

  closeDropdown() {
    if (this.hasDropdownTarget) { this.dropdownTarget.classList.remove(this.showDropdownClass) }
    this.updateMenuItemsCheckboxState()
    this.dispatch('dropdown-closed')
  }

  openDropdown() {
    this.updateMenuItemsCheckboxState()
    this.dropdownTarget.classList.add(this.showDropdownClass)
    this.filterTarget.focus()
    this.dispatch('dropdown-opened')
  }

  // ------ END ----------------------------------------------------------------

  // ------ Functions dealing with the Selection Pills state -------------------

  convertSelectionIntoPills() {
    if (this.canUpdate) {
      if (this.alphabeticalOrderValue) {
        this.clearMenuItemsSelected()
        this.addMenuItemsCheckedAsSelected()
      } else {
        this.addMenuItemsCheckedInOrder()
      }
      if (this.hasSelectionPillTemplateTarget || this.hasTagTemplateTarget) {
        this.showMenuItemsSelectedPills()
      }
    }
  }

  pillCleared(event) {
    if (this.canUpdate) {
      const itemToDelete = event.currentTarget.parentNode // TODO - Find parent dynamically because this adds dependency on the markup structure of the icon component
      this.clearSelectionByPill(itemToDelete)
    }
  }

  clearSelectionByPill(selectedPillNode) {
    if (!selectedPillNode) return

    const menuItemId = selectedPillNode.getAttribute('data-index') || selectedPillNode.getAttribute('data-tag-id')
    const menuItem = this.getMenuItemSelectedWithId(menuItemId)

    if (menuItem) {
      const menuItemChildren = this.selectMultipleListComponent?.childrenOf(menuItem) || []
      this.removeMenuItemFromSelected(menuItem, ...menuItemChildren)
      this.updateMenuItemsCheckboxState()
    }

    selectedPillNode.parentNode.removeChild(selectedPillNode)
    this.dispatch('menuItemRemoved')
    this.dispatch('menutItemsSelectedUpdated')
  }

  // ------ END ----------------------------------------------------------------

  // ------ Functions operating on top of Menu Items exclusively ---------------

  updateMenuItemsCheckboxState() {
    if (this.canUpdate) {
      this.uncheckAll()
      this.checkAllMenuItemsSelected()
      this.resolveSelectionStateForParentCheckboxes()
    }
  }

  resolveSelectionStateForParentCheckboxes() {
    this.selectMultipleListComponent?.resolveSelectionStateForInnerItems()
  }

  checkAllMenuItemsSelected() {
    if (this.canUpdate) {
      this.menuItemsSelected.forEach((menuItem) => {
        this.setCheckMenuItem(menuItem, true)
      })
    }
  }

  uncheckAll() {
    if (this.canUpdate) {
      this.menuItemTargets.forEach((menuItem) => {
        this.setCheckMenuItem(menuItem, false)
      })
    }
  }

  addMenuItemsCheckedAsSelected() {
    if (this.canUpdate) {
      this.menuItemTargets.forEach((menuItem) => {
        if (menuItem.checked) {
          this.addMenuItemAsSelected(menuItem)
        }
      })
      this.dispatch('menutItemsSelectedUpdated')
    }
  }

  addMenuItemsCheckedInOrder() {
    if (this.canUpdate) {
      this.menuItemTargets.forEach((menuItem) => {
        if (menuItem.checked && !this.menuItemsSelected.includes(menuItem)) {
          this.addMenuItemAsSelected(menuItem)
        } else if (!menuItem.checked && this.menuItemsSelected.includes(menuItem)) {
          this.removeMenuItemFromSelected(menuItem)
        }
      })
      this.dispatch('menutItemsSelectedUpdated')
    }
  }

  addMenuItemAsSelected(...items) {
    if (this.canUpdate) {
      items.forEach((item) => {
        item.checked = true
        item.disabled = !this.policy('detach?')
        this.menuItemsSelected.push(item)
      })
    }
  }

  clearMenuItemsSelected() {
    if (this.canUpdate) {
      this.menuItemsSelected = []
    }
  }

  removeMenuItemFromSelected(...items) {
    if (this.canUpdate) {
      const idsToRemove = items.map((item) => item.id.toString())

      this.menuItemsSelected = this.menuItemsSelected.filter((item) =>
        !idsToRemove.includes(item.id.toString())
      )
    }
  }

  getMenuItemSelectedWithId(menuItemId) {
    return this.menuItemsSelected.find((item) => item.id.toString() === menuItemId?.toString())
  }

  getMenuItemsSelectedValues() {
    const values = []
    this.menuItemsSelected.forEach((menuItem) => {
      values.push(menuItem.getAttribute('value'))
    })
    return values
  }

  getMenuItemCopy(item) {
    return item.getAttribute('data-select-multiple-component-menu-item-copy')
  }

  setCheckMenuItem(menuItem, checked) {
    if (this.canUpdate) {
      menuItem.checked = checked
    }
  }

  showMenuItemsSelectedPills() {
    if (this.canUpdate && this.selectionsListEnabledValue) {
      this.selectionsList.innerHTML = ''

      this.menuItemsSelected.forEach((menuItem) => {
        const menuItemId = menuItem.getAttribute('id')
        const menuItemValue = menuItem.getAttribute('value')
        const pillLabel = (
          menuItem.getAttribute('data-select-multiple-component-menu-item-pill-label') ||
          menuItem.getAttribute('data-select-multiple-component-menu-item-copy')
        )

        // Checkbox items with no HTML 'name' attribute means that they aren't counted
        // on form submissions and they serve mostly as means of interacting with the UI.
        // For that reason we skip pill for them, since we want pills to be rendered only
        // for checkboxes that hold form values and contribute to the form submission.
        const menuItemSkipPill = !menuItem.getAttribute('name')

        if (menuItemSkipPill) {
          return
        }

        if (this.hasTagTemplateTarget) {
          const tagTemplate = this.tagTemplateTarget.firstElementChild.cloneNode(true)
          tagTemplate.setAttribute('data-tag-id', menuItemId)
          tagTemplate.querySelector('*[data-tag]').setAttribute('data-tag', this.getMenuItemCopy(menuItem))
          this.tagsContainerTarget.appendChild(tagTemplate)
        } else {
          this.pillData = {
            text: this.shouldTruncatePills ? truncate(pillLabel, 23) : pillLabel,
            index: menuItemId,
            value: menuItemValue
          }

          const pill = this.selectionPillTemplate(this.pillData)
          this.selectionsList.insertAdjacentHTML('beforeend', pill)
        }
      })
    }
  }

  // ------ END ----------------------------------------------------------------
}
